import React, { useEffect, useState } from 'react';
import api from '../../services/api';

type PageIntros = {
  members: string;
  research: string;
  publications: string;
  news: string;
  recruitment: string;
  contact: string;
};

const FIELDS: { key: keyof PageIntros; label: string }[] = [
  { key: 'members', label: 'Members' },
  { key: 'research', label: 'Research' },
  { key: 'publications', label: 'Publications' },
  { key: 'news', label: 'News' },
  { key: 'recruitment', label: 'Recruitment' },
  { key: 'contact', label: 'Contact' },
];

const AdminPageIntros: React.FC = () => {
  const [data, setData] = useState<PageIntros>({
    members: '',
    research: '',
    publications: '',
    news: '',
    recruitment: '',
    contact: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get<PageIntros>('/page-intros');
      setData({
        members: res.data.members ?? '',
        research: res.data.research ?? '',
        publications: res.data.publications ?? '',
        news: res.data.news ?? '',
        recruitment: res.data.recruitment ?? '',
        contact: res.data.contact ?? '',
      });
    } catch (err) {
      console.error(err);
      setError('Load failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (key: keyof PageIntros, value: string) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await api.put('/page-intros', data);
      await load();
    } catch (err) {
      console.error(err);
      setError('Save failed. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-neutral-600">Loading...</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          Page Intro Configuration
        </h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-full bg-brand-red px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-white hover:bg-red-700 disabled:opacity-70"
        >
          {saving ? 'Saving...' : 'Save all'}
        </button>
      </div>
      <p className="mb-6 text-xs text-neutral-500">
        These texts appear at the top of corresponding pages. Leave empty to use defaults.
      </p>

      <div className="space-y-6">
        {FIELDS.map(({ key, label }) => (
          <div
            key={key}
            className="rounded-lg border border-neutral-200 bg-white p-4"
          >
            <label className="mb-2 block text-xs font-semibold text-neutral-700">
              {label}
            </label>
            <textarea
              value={data[key]}
              onChange={(e) => handleChange(key, e.target.value)}
              rows={3}
              className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
              placeholder={`${label} page intro...`}
            />
          </div>
        ))}
      </div>

      {error && <p className="mt-4 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default AdminPageIntros;
